import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Label,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { Call24Regular as PhoneIcon } from '@fluentui/react-icons';
import {
  COUNTRY_CODES,
  formatUSPhone,
  formatInternationalPhone,
  validateUSPhone,
  validateInternationalPhone,
} from '@/utils/phoneUtils';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';

interface PhoneInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  countryCode?: string;
  countryAbbr?: string;
  onCountryCodeChange?: (code: string) => void;
  isInternational?: boolean;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  showIcon?: boolean;
  validateOnBlur?: boolean;
  onValidationChange?: (result: { isValid: boolean; error?: string }) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightSemibold,
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'stretch',
    columnGap: tokens.spacingHorizontalS,
  },
  countryDropdown: {
    minWidth: '180px',
    maxWidth: '200px',
  },
  phoneInput: {
    flex: 1,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  helper: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },
  readOnlyField: {
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'not-allowed',
  },
});

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  label,
  value,
  onChange,
  countryCode = '+1',
  countryAbbr,
  onCountryCodeChange,
  isInternational = false,
  error,
  disabled = false,
  required = false,
  placeholder,
  showIcon = true,
  validateOnBlur = true,
  onValidationChange,
}) => {
  const styles = useStyles();
  const [internalError, setInternalError] = useState<string | undefined>(undefined);
  const [touched, setTouched] = useState(false);
  const [selectedAbbr, setSelectedAbbr] = useState<string>(() => {
    if (countryAbbr) {
      const abbrMatch = COUNTRY_CODES.find(c => c.abbr === countryAbbr);
      if (abbrMatch) return abbrMatch.abbr;
    }
    const match = COUNTRY_CODES.find(c => c.code === countryCode);
    return match?.abbr ?? 'US';
  });

  useEffect(() => {
    const currentMatch = COUNTRY_CODES.find(c => c.abbr === selectedAbbr);
    if (currentMatch?.code !== countryCode) {
      const newMatch = COUNTRY_CODES.find(c => c.code === countryCode);
      if (newMatch) {
        setSelectedAbbr(newMatch.abbr);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  const displayError = error || (touched ? internalError : undefined);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    if (isInternational) {
      const formatted = formatInternationalPhone(rawValue, countryCode);
      onChange(formatted);
    } else {
      const formatted = formatUSPhone(rawValue);
      onChange(formatted);
    }
  }, [isInternational, countryCode, onChange]);

  const handleBlur = useCallback(() => {
    setTouched(true);

    if (!validateOnBlur) return;

    const digits = value.replace(/\D/g, '');
    if (digits.length === 0 && !required) {
      setInternalError(undefined);
      onValidationChange?.({ isValid: true });
      return;
    }

    const result = isInternational
      ? validateInternationalPhone(value)
      : validateUSPhone(value);

    setInternalError(result.error);
    onValidationChange?.(result);
  }, [value, isInternational, validateOnBlur, required, onValidationChange]);

  const handleCountryCodeSelect = useCallback((_e: unknown, data: { optionValue?: string }) => {
    if (data.optionValue) {
      setSelectedAbbr(data.optionValue);
      const match = COUNTRY_CODES.find(c => c.abbr === data.optionValue);
      if (match && onCountryCodeChange) {
        onCountryCodeChange(match.code);
      }
    }
  }, [onCountryCodeChange]);

  useEffect(() => {
    if (touched && validateOnBlur) {
      const digits = value.replace(/\D/g, '');
      if (digits.length === 0 && !required) {
        setInternalError(undefined);
        onValidationChange?.({ isValid: true });
        return;
      }

      const result = isInternational
        ? validateInternationalPhone(value)
        : validateUSPhone(value);
      setInternalError(result.error);
      onValidationChange?.(result);
    }
  }, [value, isInternational, touched, validateOnBlur, required, onValidationChange]);

  const defaultPlaceholder = isInternational
    ? 'Enter phone number'
    : '(555) 123-4567';

  const selectedCountry = COUNTRY_CODES.find(c => c.abbr === selectedAbbr);

  return (
    <div className={styles.container}>
      <Label htmlFor={id} className={styles.labelRow}>
        {showIcon && <PhoneIcon />}
        {label}
        {required && <RequiredIndicator />}
      </Label>

      <div className={styles.inputRow}>
        {isInternational && onCountryCodeChange && (
          <Dropdown
            className={styles.countryDropdown}
            value={selectedCountry ? `${selectedCountry.code} ${selectedCountry.country}` : countryCode}
            selectedOptions={[selectedAbbr]}
            onOptionSelect={handleCountryCodeSelect}
            disabled={disabled}
          >
            {COUNTRY_CODES.map((country) => (
              <Option key={`${country.code}-${country.abbr}`} value={country.abbr} text={`${country.code} ${country.country}`}>
                {country.code} {country.country}
              </Option>
            ))}
          </Dropdown>
        )}

        <Input
          id={id}
          type="tel"
          className={`${styles.phoneInput} ${disabled ? styles.readOnlyField : ''}`}
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder={placeholder || defaultPlaceholder}
          disabled={disabled}
        />
      </div>

      {!isInternational && !displayError && (
        <span className={styles.helper}>Format: (XXX) XXX-XXXX ext. XXXX (extension optional)</span>
      )}

      {displayError && (
        <span className={styles.errorText}>{displayError}</span>
      )}
    </div>
  );
};

export default PhoneInput;
